import { useLocalization } from "@fluent/react";
import { Button, ContentCard } from "@narmi/design_system";
import { addDays } from "date-fns";
import { CreditCardImage } from "@lending/components/CreditCardImage/CreditCardImage";
import { formatShortDate } from "@lending/utils/formatters";
import { getCreditCardNetwork } from "@lending/utils/getCreditCardNetwork";
import styles from "./OfferCard.module.css";
import { BulletPoint } from "../BulletPoint/BulletPoint";
import type { CreditCardOffer } from "@lending/types";

interface Props {
  offer: CreditCardOffer;
  /** Institution-configured days an offer stays valid, counted from `offer.created_at`. */
  expirationDays: number;
  infoLinkUrl?: string;
  onPressAccept?: () => void;
  isPending?: boolean;
}
function OfferCard({ offer, expirationDays, infoLinkUrl, onPressAccept, isPending }: Props) {
  const { l10n } = useLocalization();
  const { product } = offer;
  const creditLimitLabel =
    offer.credit_limit_cents == null
      ? null
      : `$${(offer.credit_limit_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const aprLabel = offer.apr_basis == null ? null : `${(offer.apr_basis / 100).toFixed(2)}%`;
  const hasOfferMetrics = (creditLimitLabel ?? aprLabel) != null;
  // Counted from when the offer was generated, not from now: applicants return to
  // this screen days later via the loan portal, and the date must not drift.
  const expirationDate = formatShortDate(addDays(new Date(offer.created_at), expirationDays));

  return (
    <div className={styles.cardContainer}>
      <ContentCard radiusSize="l" kind="bordered" paddingSize="none">
        <div className={styles.offerCard}>
          <div className={styles.cardLeft}>
            {hasOfferMetrics && (
              <div className={styles.cardDetails}>
                {creditLimitLabel && (
                  <div className={styles.cardDetailsMetricContainer}>
                    <div className={styles.cardDetailsMetricName}>
                      {l10n.getString("credit-card-offers-limit-label", null, "LIMIT")}
                    </div>
                    <div className={styles.cardDetailsMetricNumber}>{creditLimitLabel}</div>
                  </div>
                )}
                {aprLabel && (
                  <div className={styles.cardDetailsMetricContainer}>
                    <div className={styles.cardDetailsMetricName}>
                      {l10n.getString("credit-card-offers-apr-label", null, "APR")}
                    </div>
                    <div className={styles.cardDetailsMetricNumber}>{aprLabel}</div>
                  </div>
                )}
              </div>
            )}
            <div className={styles.cardImageContainer}>
              <CreditCardImage
                width={185}
                height={116}
                network={getCreditCardNetwork(product.card_network)}
                cardName={product.name}
              />
            </div>
          </div>
          <div className={styles.cardRight}>
            <div className="fontColor--secondary fontWeight--bold fontSize--xs">
              {l10n.getString("credit-card-offers-benefits-label", null, "CARD BENEFITS")}
            </div>
            <div className={styles.bulletPoints}>
              {product.details.map((detail, index) => (
                <BulletPoint key={index} text={detail} />
              ))}
            </div>
            <div className={styles.acceptButtonContainer}>
              <Button
                size="m"
                style={{ borderRadius: "12px" }}
                endIcon="arrow-right"
                kind="primary"
                label={l10n.getString("ui-accept-offer", null, "Accept offer")}
                onClick={onPressAccept}
                isLoading={isPending}
              />
            </div>
            <div className={styles.offerCardFooter}>
              <div>
                {l10n.getString(
                  "credit-card-offers-expiration-label",
                  { date: expirationDate },
                  `Offer expires ${expirationDate}`
                )}
              </div>
              {infoLinkUrl && <div aria-hidden="true">{"·"}</div>}
              {infoLinkUrl && (
                <a
                  className={styles.infoLink}
                  href={infoLinkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={l10n.getString(
                    "ui-info-disclaimer-aria-label",
                    null,
                    "Info & disclaimer (opens in a new tab)"
                  )}
                >
                  {l10n.getString("ui-info-disclaimer", null, "Info & disclaimer")}
                </a>
              )}
            </div>
          </div>
        </div>
      </ContentCard>
    </div>
  );
}

export { OfferCard };
